import React, { useContext, useEffect, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, AppBar, Drawer, Grid, IconButton, LinearProgress, styled, Theme, useMediaQuery } from '@mui/material'
import Menu from '@mui/icons-material/Menu';
import Logo from '../Logo/Logo'
import { ActionType } from '../../Store/Navigation/actions';
import { THEME } from '../../config';
import { useHistory, useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { APIsContext } from '../../Store/APIs/context';
import { useSnackbar } from 'notistack';
import { NavigationContext } from '../../Store/Navigation/context';
import { LicenseTabs, LicensingType, SettingType, StatisticsType } from '../../Store/Navigation/state';
import Authentication from '../../Store/Authentication/AuthenticationStore';
import DrawerMenuItem from './DrawerMenuItem';
import NavigationMenuItem from './NavigationMenuItem';

const ReactiveLogoContainer = styled(Grid)(() => ({
    minHeight: "inherit",
    height: "inherit",
    padding: "0.5rem 0",
    flex: 1
}));

const ReactiveMenuContainer = styled(Grid)(() => ({
    minHeight: "inherit",
    height: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-evenly"
}));

const ReactiveNavigationContent = styled(Grid)(({ theme }) => ({
    height: "inherit",
    minHeight: "inherit",
    justifyContent: "space-around",
    alignItems: "flex-start",
    flexWrap: "nowrap",
    padding: "0 1rem",
    [theme.breakpoints.down("sm")]: {
        padding: "0"
    }
}));

const ReactiveNavigationBar = styled(AppBar)(({ theme }) => ({
    minHeight: "50px",
    height: "7vh",
    width: "100%",
    zIndex: 5,
    top: 0,
    position: "sticky",
    background: "#f8f8f8",
    borderBottom: "solid",
    borderBottomWidth: "1px",
    borderBottomColor: "#dce1e6",
    display: "block",
    [theme.breakpoints.down("md")]: {
        height: "5vh",
    }
}));

const MobileAccordion = styled(Accordion)(() => ({
    boxShadow: "none",
    borderRadius: "0px",
    borderBottom: "solid",
    borderBottomWidth: "1px",
    borderBottomColor: "#dce1e6",
    width: "100%"
}))

const MobileAccordionSummary = styled(AccordionSummary)(() => ({
    padding: "0px",
    borderBottomColor: "#a8a8a8",
    borderBottomStyle: "solid",
    borderBottomWidth: "0px",
    margin: "0 0 0 0"
}))

const MobileAccordionDetails = styled(AccordionDetails)(() => ({
    display: "flex",
    flexDirection: "column",
    padding: 0,
    width: "100%",
}))

const Navigation: React.FC = (props) => {

    const { enqueueSnackbar } = useSnackbar();
    const { state, dispatch } = useContext(NavigationContext)
    const apiState = useContext(APIsContext).state
    const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const [mobileDrawer, setMobileDrawer] = useState(false)
    const [settingDrawerAccordion, setSettingDrawerAccordion] = useState(false)
    const [statisticsDrawerAccordion, setstatisticsDrawerAccordion] = useState(false)
    const [licensingDrawerAccordion, setlicensingDrawerAccordion] = useState(false)
    const [optionsActive, setOptionsActive] = useState(true)
    const [licensingTabsActive, setLicensingTabsActive] = useState<LicenseTabs>({usage: false, cloud: false, internetLicensing: false, dongles: false})
    const [developerOptionsActive, setDeveloperOptionsActive] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const history = useHistory();
    const { pathname } = useLocation();

    useEffect(() => {
        dispatch({ type: ActionType.ToggleMobileMenu, toggle: matches })
    }, [matches, dispatch])

    useEffect(() => {
        setOptionsActive(state.optionsActive)
    }, [state.optionsActive])
    
    useEffect(() => {
        if ((apiState.ImagesApi as any).configuration === undefined) {
            history.push("/login")
        }
    }, [apiState, history])

    useEffect(() => {
        if (state.licensingTabs.cloud === false && state.licensingTabs.dongles === false &&
            state.licensingTabs.internetLicensing === false && state.licensingTabs.usage === false) {
                enqueueSnackbar("Cannot connect to licensing server", {variant: "error"})
            }
    }, [state.licensingTabs, enqueueSnackbar])

    useEffect(() => {
        setIsLoading(state.isLoading)
    }, [state.isLoading])

    useEffect(() => {
        setDeveloperOptionsActive(state.developerOptions)
    }, [state.developerOptions])

    useEffect(() => {
        setLicensingTabsActive(state.licensingTabs)
    }, [state.licensingTabs])

    const handleMenuItemClick = (
        route: string,
        drawer?: boolean,
        settingType?: SettingType,
        licensingType?: LicensingType,
        statisticsType?: StatisticsType
      ) => {
        history.push(route);
        if (drawer) setMobileDrawer(false);
        if (settingType) {
            dispatch({ type: ActionType.SetSettingType, settingType });
        }
        if (licensingType) {
            dispatch({ type: ActionType.SetLicensingType, licensingType });
        }
        if (statisticsType) {
            dispatch({ type: ActionType.SetStatisticsType, statisticsType });
        }
    };

    const getSettingAccordion = () => {
        return (
            <MobileAccordion
                expanded={settingDrawerAccordion}
                onChange={() => setSettingDrawerAccordion(!settingDrawerAccordion)}
            >
                <MobileAccordionSummary
                    expandIcon={
                        <ExpandMoreIcon fontSize='medium' style={{color: THEME.palette.primary.main}}/>
                    }
                    id="Accordion"
                >
                    <DrawerMenuItem name="Settings" disableRipple />
                </MobileAccordionSummary>
                <MobileAccordionDetails>
                    <DrawerMenuItem name="Capturing" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.CAPTURING)}/>
                    <DrawerMenuItem name="Detection" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.DETECTION)}/>
                    <DrawerMenuItem name="Tracking" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.TRACKING)}/>
                    <DrawerMenuItem name="Face quality" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.FACE_QUALITY)}/>
                    <DrawerMenuItem name="Verification" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.VERIFICATION)}/>
                    <DrawerMenuItem name="Liveness" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.LIVENESS)}/>
                    <DrawerMenuItem name="Attributes" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.ATTRIBUTES)}/>
                    <DrawerMenuItem name="ICAO" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.ICAO)}/>
                    <DrawerMenuItem name="Management" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.MANAGEMENT)}/>
                    <DrawerMenuItem name="Accounts" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.ACCOUNTS)}/>
                    <DrawerMenuItem name="Adjudication" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.ADJUDICATION)}/>
                    {developerOptionsActive ? <DrawerMenuItem name="Developer" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.DEVELOPER)}/> : null }
                    <DrawerMenuItem name="Reset" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.RESET)}/>
                    <DrawerMenuItem name="About" sub onClick={() => handleMenuItemClick("/settings", true, SettingType.ABOUT)} />
                </MobileAccordionDetails>
            </MobileAccordion>
        )
    }

    const getStatisticsAccordrion = () => {
        return (
            <MobileAccordion
                expanded={statisticsDrawerAccordion}
                onChange={() => setstatisticsDrawerAccordion(!statisticsDrawerAccordion)}
            >
                <MobileAccordionSummary
                    expandIcon={
                        <ExpandMoreIcon fontSize='medium' style={{color: THEME.palette.primary.main}}/>
                    }
                    id="Accordion"
                >
                    <DrawerMenuItem name="Statistics" disableRipple/>
                </MobileAccordionSummary>
                <MobileAccordionDetails>
                    <DrawerMenuItem name="MMID Statistics" sub onClick={() => handleMenuItemClick("/statistics", true, undefined, undefined, StatisticsType.MMID)}/>
                    <DrawerMenuItem name="System Statistics" sub onClick={() => handleMenuItemClick("/statistics", true, undefined, undefined, StatisticsType.SYSTEM)}/>
                </MobileAccordionDetails>
            </MobileAccordion>
        )
    }

    const getLicensingAccordrion = () => {
        return (
            <MobileAccordion
                expanded={licensingDrawerAccordion}
                onChange={() => setlicensingDrawerAccordion(!licensingDrawerAccordion)}
            >
                <MobileAccordionSummary
                    expandIcon={
                        <ExpandMoreIcon fontSize='medium' style={{color: THEME.palette.primary.main}}/>
                    }
                    id="Accordion"
                >
                    <DrawerMenuItem name="Licensing" disableRipple />
                </MobileAccordionSummary>
                <MobileAccordionDetails>
                    {<DrawerMenuItem name="Usage" sub onClick={() => handleMenuItemClick("/licensing", true, undefined, LicensingType.USAGE)}/>}
                    {licensingTabsActive.cloud?             <DrawerMenuItem name="Cloud" sub onClick={() => handleMenuItemClick("/licensing", true, undefined, LicensingType.CLOUD)}/> : null}
                    {licensingTabsActive.internetLicensing? <DrawerMenuItem name="Internet licensing" sub onClick={() => handleMenuItemClick("/licensing", true, undefined, LicensingType.INTERNET_LICENSING)}/> : null}
                    {licensingTabsActive.dongles?           <DrawerMenuItem name="Dongles" sub onClick={() => handleMenuItemClick("/licensing", true, undefined, LicensingType.DONGLES)}/> : null}
                </MobileAccordionDetails>
            </MobileAccordion>
        )
    }

    const navPicker = () => {
        if (!optionsActive) {
            return (<></>)
        }
        if (state.isMobile) {
            return (
                <IconButton sx={{ color: THEME.palette.primary.main }} onClick={() => setMobileDrawer(true)}>
                    <Menu fontSize="large" />
                </IconButton>
            )
        } else {
            return (
                <React.Fragment>
                    {Authentication.isAdminAuthenticated() && (<NavigationMenuItem name="Subjects" onClick={() => handleMenuItemClick("/subjects")} selected={pathname === "/subjects"}/>)}
                    {Authentication.isAdminAuthenticated() && (<NavigationMenuItem name="Operations" onClick={() => handleMenuItemClick("/operations")} selected={pathname === "/operations"}/>)}
                    {Authentication.isOnlyAdjudicatorAuthenticated() && (<NavigationMenuItem name="Adjudication" onClick={() => handleMenuItemClick("/adjudication")} selected={pathname === "/adjudication"}/>)}
                    <IconButton sx={{ color: THEME.palette.primary.main }} onClick={() => setMobileDrawer(true)}>
                        <Menu fontSize="large" />
                    </IconButton>                    
                </React.Fragment>
            )
        }
    }

    const getDrawer = () => {
        return (
            <Drawer
                anchor="right"
                open={mobileDrawer}
                onClose={() => setMobileDrawer(false)}
                variant='temporary'
            >
                {Authentication.isAdminAuthenticated() && state.isMobile && (<DrawerMenuItem name="Subjects" onClick={() => handleMenuItemClick("/subjects", true)} slightBorder />)}
                {Authentication.isAdminAuthenticated() && state.isMobile && (<DrawerMenuItem name="Operations" onClick={() => handleMenuItemClick("/operations", true)} slightBorder/>)}
                {Authentication.isAdminAuthenticated() && (<DrawerMenuItem name="Exceptions" onClick={() => handleMenuItemClick("/exceptions", true)}slightBorder/>)}
                {Authentication.isAdminAuthenticated() && getStatisticsAccordrion()}
                {Authentication.isAdminAuthenticated() && (<DrawerMenuItem name="Janus" onClick={() => handleMenuItemClick("/janus", true)} slightBorder/>)}
                {(Authentication.isAdminAuthenticated() || (state.isMobile && Authentication.isOnlyAdjudicatorAuthenticated())) && (<DrawerMenuItem name="Adjudication" onClick={() => handleMenuItemClick("/adjudication", true)} slightBorder/>)}
                {Authentication.isAdminAuthenticated() && getLicensingAccordrion()}
                {Authentication.isAdminAuthenticated()? getSettingAccordion() : (<DrawerMenuItem name="Info" onClick={() => handleMenuItemClick("/info", true)} slightBorder/>)}
            </Drawer>
        )
    } 

    return (
        <>
            {getDrawer()}
            <ReactiveNavigationBar position="static" elevation={0}>
                <ReactiveNavigationContent container spacing={0}>
                    <ReactiveLogoContainer item>
                        <Logo nav/>
                    </ReactiveLogoContainer>
                    <ReactiveMenuContainer item>
                        {navPicker()}
                    </ReactiveMenuContainer>
                </ReactiveNavigationContent>
                {isLoading ? <LinearProgress style={{zIndex: "1000"}} color="primary" /> : null}
            </ReactiveNavigationBar>
        </>
    )
}

export default Navigation